import { loadFont } from "helpers/fonts";
import { createUrl } from "helpers/url";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";

export interface GoogleFontSearchParams {
    /**
     * The API key for accessing Google Fonts API.
     */
    key: string;
    /**
     * The font format to load.
     */
    capability?: "WOFF2" | "VF";
    /**
     * The category of fonts to filter by.
     */
    category?: "serif" | "sans-serif" | "display" | "handwriting" | "monospace";
    /**
     * The sorting method to use when retrieving fonts.
     */
    sort?: "alpha" | "style" | "popularity" | "trending" | "date";
    /**
     * And a list of font families to filter by.
     */
    family?: string[];
}

export interface GoogleFontResponse {
    family: string;
    variants: string[];
    subsets: string[];
    version: string;
    lastModified: string;
    category: string;
    files: {
        [key: string]: string;
    };
    menu: string;
}

export interface GoogleFontsResponse {
    kind: string;
    items: GoogleFontResponse[];
}

const GOOGLE_FONT_API_KEY = import.meta.env.VITE_GOOGLE_FONT_API_KEY;

export interface UseGoogleFontFamilyOptions
    extends Omit<GoogleFontSearchParams, "key"> {
    /**
     * Additional font families to always load (regardless of category filter).
     */
    additionalFonts?: string[];
    /**
     * Font families to exclude/filter out from the results.
     */
    excludeFonts?: string[];
}

/**
 * Custom hook to load Google Fonts.
 */
export const useLoadGoogleFontFamily = (
    {
        capability,
        category,
        sort,
        family,
        additionalFonts,
        excludeFonts
    }: UseGoogleFontFamilyOptions = {
        capability: "WOFF2",
        category: "monospace",
        sort: "alpha",
        family: [],
        additionalFonts: [],
        excludeFonts: []
    }
) => {
    const [availableFonts, setAvailableFonts] = useState<GoogleFontResponse[]>(
        []
    );
    const [loadedFonts, setLoadedFonts] = useState<GoogleFontResponse[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const processing = useRef<boolean>(false);

    const ready = useMemo(
        () => loadedFonts.length > 0 && !error,
        [error, loadedFonts.length]
    );

    const loading = useMemo(() => processing.current && !ready, [ready]);

    const GOOGLE_FONT_API_URL = useMemo(
        () =>
            createUrl<GoogleFontSearchParams>("https://www.googleapis.com", {
                pathname: "webfonts/v1/webfonts",
                searchParams: {
                    key: GOOGLE_FONT_API_KEY,
                    capability,
                    category,
                    sort,
                    family
                }
            }),
        [capability, category, family, sort]
    );

    const loadFonts = useCallback(async (fonts: GoogleFontResponse[]) => {
        await Promise.all(
            fonts.map(async (font) => {
                const { family, files } = font;

                // Only load the regular variant - italic and bold are not an option
                if (!files["regular"]) return;

                await loadFont(family, files["regular"]);
                setLoadedFonts((prev) => [...prev, font]);
            })
        );
    }, []);

    const additionalFontsString = useMemo(
        () => additionalFonts?.join(",") || "",
        [additionalFonts]
    );

    const excludeFontsString = useMemo(
        () => excludeFonts?.join(",") || "",
        [excludeFonts]
    );

    useEffect(() => {
        const getAvailableFonts = async () => {
            if (!GOOGLE_FONT_API_KEY)
                throw new Error("Google API key is not set");

            if (processing.current) return;
            try {
                processing.current = true;
                const response = await fetch(GOOGLE_FONT_API_URL.href);

                if (!response.ok)
                    throw new Error(
                        (await response.json()).message ||
                            "Failed to fetch Google Fonts"
                    );

                const _availableFonts =
                    (await response.json()) as GoogleFontsResponse;

                // Filter out excluded fonts from the category results
                let filteredCategoryFonts = _availableFonts.items;
                if (excludeFonts && excludeFonts.length > 0) {
                    filteredCategoryFonts = _availableFonts.items.filter(
                        (font) => !excludeFonts.includes(font.family)
                    );
                }

                let allAvailableFonts = filteredCategoryFonts;

                // Always load all fonts from the requested category (after filtering)
                await loadFonts(filteredCategoryFonts);

                // Load additional fonts if specified
                if (additionalFonts && additionalFonts.length > 0) {
                    const additionalFontsUrl =
                        createUrl<GoogleFontSearchParams>(
                            "https://www.googleapis.com",
                            {
                                pathname: "webfonts/v1/webfonts",
                                searchParams: {
                                    key: GOOGLE_FONT_API_KEY,
                                    family: additionalFonts
                                }
                            }
                        );

                    const additionalResponse = await fetch(
                        additionalFontsUrl.href
                    );

                    if (!additionalResponse.ok)
                        throw new Error(
                            (await additionalResponse.json()).message ||
                                "Failed to fetch additional Google Fonts"
                        );

                    const additionalData =
                        (await additionalResponse.json()) as GoogleFontsResponse;
                    if (additionalData.items.length > 0) {
                        await loadFonts(additionalData.items);
                        // Add additional fonts to available fonts list
                        const fontMap = new Map();
                        [...additionalData.items, ...allAvailableFonts].forEach(
                            (font) => {
                                fontMap.set(font.family, font);
                            }
                        );
                        allAvailableFonts = Array.from(fontMap.values());
                    }
                }

                setAvailableFonts(allAvailableFonts);
            } catch (error) {
                setError(error);
            } finally {
                processing.current = false;
            }
        };

        getAvailableFonts();

        // Leverage just the font string to prevent unstable re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        GOOGLE_FONT_API_URL.href,
        loadFonts,
        additionalFontsString,
        excludeFontsString
    ]);

    return {
        /**
         * Array of available fonts. All fonts are automatically loaded.
         */
        availableFonts,
        /**
         * Indicates if fonts are ready to use.
         */
        ready,
        /**
         * Indicates if fonts are currently being loaded.
         */
        loading,
        /**
         * Indicates if loading fonts had an error.
         */
        error
    };
};
